/**
 * LIS / analyzers FeatureModule — the SIL connection surface: onboarding wizard, device-status
 * dashboard, ingestion/quarantine monitor, and positional-load confirmation. Routes mount under the
 * authenticated AppShell; the router auto-wraps each in <ProtectedRoute requiredCapabilities>. Every
 * mutating action is additionally gated client-side via useCan (server re-checks each request).
 *
 * To register: add `import lisModule from "@/features/lis";` + `lisModule` to the MODULES array in
 * src/app/registry.ts (the ONLY router-side edit — Integrate does this; features never touch it).
 */
import type { FeatureModule } from "@/app";
import { ProtectedRoute } from "@/auth";
import { CAP, MODULE_CAPS } from "./api";
import { registerLisMessages } from "./i18n";
import { DevicesDashboardPage } from "./DevicesDashboardPage";
import { OnboardingWizardPage } from "./OnboardingWizardPage";
import { MonitorPage } from "./MonitorPage";
import { ChargementPage } from "./ChargementPage";
import { AgentsPage } from "./AgentsPage";

const lisModule: FeatureModule = {
  id: "lis",
  title: "Automates",
  // Module gate: the minimum capability to reach any LIS screen (server cap key).
  requiredCapabilities: MODULE_CAPS,
  routes: [
    { path: "/lis", element: <DevicesDashboardPage /> },
    { path: "/lis/raccorder", element: <OnboardingWizardPage /> },
    { path: "/lis/flux", element: <MonitorPage /> },
    { path: "/lis/chargement", element: <ChargementPage /> },
    // NOVA-070 — the cloud LIS relay-agent cockpit. The router wraps every route with the MODULE caps
    // (lis.automate.connect); the agent cockpit needs the higher lis.agent.manage, so it carries its OWN
    // ProtectedRoute (lab_admin/manager + owner). The server hard-gates every /lis/agents request too.
    {
      path: "/lis/agents",
      element: (
        <ProtectedRoute requiredCapabilities={[CAP.agentManage]}>
          <AgentsPage />
        </ProtectedRoute>
      ),
    },
  ],
  nav: [
    {
      id: "lis-dashboard",
      path: "/lis",
      label: "nav.lis.dashboard",
      icon: "▤",
      order: 40,
      section: "nav.section.lis",
    },
    {
      id: "lis-onboarding",
      path: "/lis/raccorder",
      label: "nav.lis.onboarding",
      icon: "＋",
      order: 41,
      section: "nav.section.lis",
      // Only show the wizard entry to users who may actually configure an adapter.
      requiredCapabilities: [CAP.configure],
    },
    {
      id: "lis-monitor",
      path: "/lis/flux",
      label: "nav.lis.monitor",
      icon: "❚❚",
      order: 42,
      section: "nav.section.lis",
      requiredCapabilities: [CAP.messageRead],
    },
    {
      id: "lis-chargement",
      path: "/lis/chargement",
      label: "nav.lis.chargement",
      icon: "▦",
      order: 43,
      section: "nav.section.lis",
      requiredCapabilities: [CAP.chargementRead],
    },
    {
      id: "lis-agents",
      path: "/lis/agents",
      label: "nav.lis.agents",
      icon: "◫",
      order: 44,
      section: "nav.section.lis",
      // Only the relay-fleet steward (lab_admin/manager + owner) sees the cockpit entry.
      requiredCapabilities: [CAP.agentManage],
    },
  ],
  onRegister() {
    registerLisMessages();
  },
};

export default lisModule;
